import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const generalCapabilities = [
  '概括课堂内容',
  '回答提问',
  '生成建议',
  '灵活的语言与理解能力',
];

const professionalCapabilities = [
  { label: '长视频切片', desc: '40-45分钟有效切片与角色识别' },
  { label: '多模态对齐', desc: '文本、声音、画面三类互证' },
  { label: '知识图谱', desc: '学科知识底座+教学逻辑图谱' },
  { label: '专家样本', desc: '指标映射与专业判断学习' },
  { label: '多维输出', desc: '总分/维度分/分档/证据链' },
  { label: '群体分析', desc: '教师成长→学校质量→区域趋势' },
];

export default function P44Comparison({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14">
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className="inline-block text-lg font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700">定位</span>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 transition-all duration-1000 delay-100">
            通用大模型点评与<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">专业评课系统</span>的差别
          </h2>
        </div>

        {/* 左右对比 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-6xl mx-auto mb-6 md:mb-8 transition-all duration-1000 delay-200">
          {/* 通用大模型 */}
          <div className="p-6 md:p-7 rounded-2xl glass border-white/50">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-secondary-100/50 flex items-center justify-center">
                <i className="ri-robot-line text-xl text-secondary-600" />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">通用大模型</h3>
            </div>
            <div className="space-y-2.5">
              {generalCapabilities.map((cap) => (
                <div key={cap} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-background-50/60">
                  <i className="ri-check-line text-secondary-500 text-lg" />
                  <span className="text-lg text-foreground-950 font-body">{cap}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-background-200/60">
              <p className="text-base md:text-lg text-foreground-950 font-body">
                提供灵活的语言与理解能力，但缺少标准、样本、证据、验证和汇总的工程化链路
              </p>
            </div>
          </div>

          {/* 专业评课系统 */}
          <div className="p-6 md:p-7 rounded-2xl glass-liquid border-primary-200/30">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl glass-blue border-primary-200/40 flex items-center justify-center">
                <i className="ri-brain-line text-xl text-primary-600" />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">专业评课系统</h3>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {professionalCapabilities.map((cap) => (
                <div key={cap.label} className="p-3 rounded-lg glass-blue border-primary-200/30">
                  <p className="text-lg font-body font-bold text-primary-700">{cap.label}</p>
                  <p className="text-base text-foreground-950 font-body mt-1">{cap.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto text-center transition-all duration-1000 delay-600">
          <div className="inline-flex items-center gap-2.5 px-5 py-3 rounded-xl glass-gold border-accent-200/30">
            <i className="ri-information-line text-accent-500 text-lg" />
            <span className="text-lg font-body text-foreground-950">
              差别主要在应用目标、数据基础和工程体系
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}